import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ConversationDto, ChatMessageDto } from '@api/types/chat'
import { ConversationType } from '@api/types/chat'

interface MessagesState {
  items: ChatMessageDto[]
  page: number
  hasMore: boolean
  loading: boolean
}

type ConversationFilter = 'all' | 'direct' | 'groups' | 'adventures'

interface ChatState {
  // Conversations
  conversations: ConversationDto[]
  activeConversationId: string | null
  conversationsLoading: boolean
  conversationsError: string | null

  // Messages keyed by conversation ID
  messages: Record<string, MessagesState>

  // Notifications
  unreadCount: number

  // UI State
  sidebarCollapsed: boolean
  conversationFilter: ConversationFilter
  popouts: string[] // Array of popped-out conversation IDs
  suppressedPopouts: Set<string> // Conversations displayed inline (e.g., lobby chat) — skip popout

  // Actions
  setConversations: (conversations: ConversationDto[]) => void
  setActiveConversation: (conversationId: string | null) => void
  setConversationsLoading: (loading: boolean) => void
  setConversationsError: (error: string | null) => void

  // Message actions
  setMessages: (conversationId: string, messages: ChatMessageDto[], page: number, hasMore: boolean) => void
  addMessage: (conversationId: string, message: ChatMessageDto) => void
  updateMessage: (conversationId: string, messageId: string, updates: Partial<ChatMessageDto>) => void
  deleteMessage: (conversationId: string, messageId: string) => void
  setMessagesLoading: (conversationId: string, loading: boolean) => void
  appendMessages: (conversationId: string, messages: ChatMessageDto[], page: number, hasMore: boolean) => void

  // Conversation updates
  updateConversation: (conversationId: string, updates: Partial<ConversationDto>) => void
  addConversation: (conversation: ConversationDto) => void
  removeConversation: (conversationId: string) => void
  markConversationRead: (conversationId: string) => void

  // Notification actions
  setUnreadCount: (count: number) => void
  incrementUnreadCount: () => void
  decrementUnreadCount: (amount?: number) => void

  // UI actions
  toggleSidebarCollapse: () => void
  setConversationFilter: (filter: ConversationFilter) => void
  addPopout: (conversationId: string) => void
  removePopout: (conversationId: string) => void
  suppressPopout: (conversationId: string) => void
  unsuppressPopout: (conversationId: string) => void

  // Reset
  reset: () => void
}

const initialState = {
  conversations: [],
  activeConversationId: null,
  conversationsLoading: false,
  conversationsError: null,
  messages: {},
  unreadCount: 0,
  sidebarCollapsed: false,
  conversationFilter: 'all' as ConversationFilter,
  popouts: [],
  suppressedPopouts: new Set<string>(),
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Conversation actions
      setConversations: (conversations) => set({ conversations }),

      setActiveConversation: (conversationId) => set({ activeConversationId: conversationId }),

      setConversationsLoading: (loading) => set({ conversationsLoading: loading }),

      setConversationsError: (error) => set({ conversationsError: error }),

      // Message actions
      setMessages: (conversationId, items, page, hasMore) =>
        set((state) => ({
          messages: {
            ...state.messages,
            [conversationId]: { items, page, hasMore, loading: false },
          },
        })),

      addMessage: (conversationId, message) =>
        set((state) => {
          const currentMessages = state.messages[conversationId] || { items: [], page: 1, hasMore: true, loading: false }
          const isActive = state.activeConversationId === conversationId

          // Update the conversation's last message info and unread count
          const updatedConversations = state.conversations.map((conv) =>
            conv.id === conversationId
              ? {
                  ...conv,
                  lastMessageAt: message.timestamp,
                  lastMessageContent: message.content,
                  // Increment unread count for non-active conversations (unless it's our own message)
                  ...(!isActive && !message.isOwnMessage
                    ? { unreadMessageCount: conv.unreadMessageCount + 1 }
                    : {}),
                }
              : conv
          )

          return {
            messages: {
              ...state.messages,
              [conversationId]: {
                ...currentMessages,
                items: [...currentMessages.items, message],
              },
            },
            conversations: updatedConversations,
          }
        }),

      updateMessage: (conversationId, messageId, updates) =>
        set((state) => {
          const currentMessages = state.messages[conversationId]
          if (!currentMessages) return state

          return {
            messages: {
              ...state.messages,
              [conversationId]: {
                ...currentMessages,
                items: currentMessages.items.map((msg) =>
                  msg.id === messageId ? { ...msg, ...updates } : msg
                ),
              },
            },
          }
        }),

      deleteMessage: (conversationId, messageId) =>
        set((state) => {
          const currentMessages = state.messages[conversationId]
          if (!currentMessages) return state

          return {
            messages: {
              ...state.messages,
              [conversationId]: {
                ...currentMessages,
                items: currentMessages.items.map((msg) =>
                  msg.id === messageId ? { ...msg, isDeleted: true } : msg
                ),
              },
            },
          }
        }),

      setMessagesLoading: (conversationId, loading) =>
        set((state) => ({
          messages: {
            ...state.messages,
            [conversationId]: {
              ...(state.messages[conversationId] || { items: [], page: 1, hasMore: true }),
              loading,
            },
          },
        })),

      appendMessages: (conversationId, newMessages, page, hasMore) =>
        set((state) => {
          const currentMessages = state.messages[conversationId] || { items: [], page: 1, hasMore: true, loading: false }
          return {
            messages: {
              ...state.messages,
              [conversationId]: {
                items: [...newMessages, ...currentMessages.items], // Prepend older messages
                page,
                hasMore,
                loading: false,
              },
            },
          }
        }),

      // Conversation update actions
      updateConversation: (conversationId, updates) =>
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId ? { ...conv, ...updates } : conv
          ),
        })),

      addConversation: (conversation) =>
        set((state) => ({
          conversations: [conversation, ...state.conversations],
        })),

      removeConversation: (conversationId) =>
        set((state) => ({
          conversations: state.conversations.filter((conv) => conv.id !== conversationId),
          activeConversationId:
            state.activeConversationId === conversationId ? null : state.activeConversationId,
        })),

      markConversationRead: (conversationId) =>
        set((state) => {
          const conversation = state.conversations.find((c) => c.id === conversationId)
          const unreadToRemove = conversation?.unreadMessageCount || 0

          return {
            conversations: state.conversations.map((conv) =>
              conv.id === conversationId ? { ...conv, unreadMessageCount: 0 } : conv
            ),
            unreadCount: Math.max(0, state.unreadCount - unreadToRemove),
          }
        }),

      // Notification actions
      setUnreadCount: (count) => set({ unreadCount: count }),

      incrementUnreadCount: () => set((state) => ({ unreadCount: state.unreadCount + 1 })),

      decrementUnreadCount: (amount = 1) =>
        set((state) => ({ unreadCount: Math.max(0, state.unreadCount - amount) })),

      // UI actions
      toggleSidebarCollapse: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setConversationFilter: (filter) => set({ conversationFilter: filter }),

      addPopout: (conversationId) =>
        set((state) => {
          if (state.popouts.includes(conversationId)) return state
          // Skip if this conversation is displayed inline (e.g., lobby chat)
          if (state.suppressedPopouts.has(conversationId)) return state
          // Max 2 popouts
          const newPopouts = [...state.popouts, conversationId].slice(-2)
          return { popouts: newPopouts }
        }),

      removePopout: (conversationId) =>
        set((state) => ({
          popouts: state.popouts.filter((id) => id !== conversationId),
        })),

      suppressPopout: (conversationId) =>
        set((state) => {
          const next = new Set(state.suppressedPopouts)
          next.add(conversationId)
          return {
            suppressedPopouts: next,
            // Also remove any existing popout for this conversation
            popouts: state.popouts.filter((id) => id !== conversationId),
          }
        }),

      unsuppressPopout: (conversationId) =>
        set((state) => {
          const next = new Set(state.suppressedPopouts)
          next.delete(conversationId)
          return { suppressedPopouts: next }
        }),

      // Reset
      reset: () => set(initialState),
    }),
    {
      name: 'adventurecore-chat',
      partialize: (state) => ({
        // Persist UI preferences and active conversation
        sidebarCollapsed: state.sidebarCollapsed,
        conversationFilter: state.conversationFilter,
        activeConversationId: state.activeConversationId,
      }),
    }
  )
)

// Selector helpers
export const useActiveConversation = () => {
  const { conversations, activeConversationId } = useChatStore()
  return conversations.find((c) => c.id === activeConversationId) || null
}

export const useConversationsByType = (type: ConversationType) => {
  const { conversations } = useChatStore()
  return conversations
    .filter((c) => c.type === type)
    .sort((a, b) => {
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
      return bTime - aTime
    })
}

// Filter + sort all conversations by the active filter
export const useFilteredConversations = () => {
  const { conversations, conversationFilter } = useChatStore()

  const filtered = conversationFilter === 'all'
    ? conversations
    : conversations.filter((c) => {
        switch (conversationFilter) {
          case 'direct': return c.type === ConversationType.Direct
          case 'groups': return c.type === ConversationType.Group
          case 'adventures': return c.type === ConversationType.Adventure
          default: return true
        }
      })

  return [...filtered].sort((a, b) => {
    const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
    const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
    return bTime - aTime
  })
}

export const useConversationMessages = (conversationId: string) => {
  const { messages } = useChatStore()
  return messages[conversationId] || { items: [], page: 1, hasMore: true, loading: false }
}
